import styled, { css, RuleSet } from 'styled-components';
import { COLOURS, mediaMap, shouldForwardProp } from '@belong/themes';
import { FlexAlignmentProperties, StyledCSS } from '@belong/types';
import { ISkeletonProps } from './Skeleton.types';

const { GREY_300, GREY_400 } = COLOURS;

const getStyles = (): StyledCSS<ISkeletonProps> => css`
  background-color: ${GREY_300};

  animation: shimmer 2s ease-in-out infinite;
  background-image: linear-gradient(90deg, transparent 0%, ${GREY_400} 33%, ${GREY_400} 66%, transparent 100%);

  // reduced motion users
  @media (prefers-reduced-motion: reduce) {
    animation: fade 3s linear infinite;
    background-image: none;
  }

  // high contrast users
  @media (forced-colors: active) {
    background-color: Canvas;
    outline: 1px solid CanvasText;
  }

  background-size: 800px 100%;
  background-repeat: no-repeat;
  display: inline-block;
  line-height: 1;
  width: 100%;

  @keyframes fade {
    0% {
      background-color: ${GREY_300};
    }
    50% {
      background-color: ${GREY_400};
    }
    100% {
      background-color: ${GREY_300};
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -800px 0;
    }
    100% {
      background-position: calc(800px + 100%) 0;
    }
  }
`;

export const SkeletonPulse = styled.div.withConfig({
  shouldForwardProp: shouldForwardProp<ISkeletonProps>(['height', 'width'])
})<ISkeletonProps>`
  display: inline-block;

  ${getStyles}
`;

const getBaseStyles = ({ circle, height, width }: ISkeletonProps): StyledCSS<ISkeletonProps> => {
  const widthStyles = mediaMap(
    width || '100%',
    (value: string) => css`
      width: ${value};
    `
  );
  const heightStyles = mediaMap(
    height || 'auto',
    (value: string) => css`
      height: ${value};
    `
  );

  return circle
    ? css`
        ${widthStyles};
        ${heightStyles};
        border-radius: 50%;
      `
    : css`
        ${widthStyles};
        ${heightStyles};
        border-radius: 3px;
      `;
};

export const SkeletonLine = styled(SkeletonPulse)<ISkeletonProps>`
  ${getBaseStyles};
  display: block;
  min-height: 1em;
`;

export const Spacer = styled.div`
  margin-bottom: 1rem;
`;

interface ILayoutProps {
  marginBottom?: string;
  spaceBetween?: boolean;
  alignItems?: FlexAlignmentProperties;
  justifyContent?: FlexAlignmentProperties;
}

export const Layout = styled.div<ILayoutProps>`
  position: relative;
  display: flex;
  ${({ spaceBetween }: ILayoutProps): RuleSet =>
    spaceBetween
      ? css`
          justify-content: space-between;
        `
      : css``}
  ${({ alignItems }: ILayoutProps): RuleSet =>
    alignItems
      ? css`
          align-items: ${alignItems};
        `
      : css``}
  ${({ justifyContent }: ILayoutProps): RuleSet =>
    justifyContent
      ? css`
          justify-content: ${justifyContent};
        `
      : css``}
  margin-bottom: ${({ marginBottom }): string | StyledCSS => marginBottom || `0`};
`;
